import React from 'react'
import { useState, useEffect, useMemo } from 'react'
import {
  Grid,
  Layout,
  Input,
  Button,
  Dialog,
  Spinner,
  useToaster,
} from '@enterprise-ui/canvas-ui-react'
import EnterpriseIcon, { TrashIcon } from '@enterprise-ui/icons'
import { useAuth } from '@praxis/component-auth'
import { useAnalytics } from '@praxis/analytics/client'
import { SectionHeader } from '../../globalComponents/SectionHeader'
import { VendorContext } from '../../globalState/Vendor/index'
import {
  getCustomLocationGroupOptions,
  getCustomLocationGroupById,
  createCustomLocationGroup,
  deleteCustomLocationGroup,
  updateCustomLocationGroup,
} from '../../services/requests'
import { AutocompleteFilter } from '../../globalComponents/AutocompleteFilter'
import {
  MANAGE_CLG_GROUP_LEVEL_OPTIONS,
  COST_LOCATION_GROUP_TYPE,
  COST_LOCATION_GROUP_VALUES,
  CUSTOM_ANALYTICS_TYPES,
} from '../../globalConfig/constants'
import { containsSpecialChars } from '../../services/helperFunctions'
import isEqual from 'lodash/isEqual'

export const ManageCustomLocationGroupsPage = () => {
  const auth = useAuth()
  const userLanId = auth?.session?.userInfo?.lanId
  const { sendEvent } = useAnalytics()
  const [state] = React.useContext(VendorContext)
  const [waitingOnClgOptionsResponse, setWaitingOnClgOptionsResponse] =
    useState(false)
  const [waitingOnResponse, setWaitingOnResponse] = useState(false)
  const [clgOptions, setClgOptions] = useState([])
  const [selectedClg, setSelectedClg] = useState([])
  const [existingClgDetails, setExistingClgDetails] = useState(null)
  const [groupTitle, setGroupTitle] = useState('')
  const [groupDescription, setGroupDescription] = useState('')
  const [groupLevel, setGroupLevel] = useState([])
  const [groupValues, setGroupValues] = useState([])
  const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState(false)
  const makeToast = useToaster()
  const groupLevelOptions = useMemo(() => constructGroupLevelOptions(), [])

  const dvsStatus = state.dvsStatus[state.selectedVendor]

  const GROUP_TITLE_MAX_CHARS = 100
  const GROUP_DESC_MAX_CHARS = 250

  useEffect(() => {
    sendEvent({
      event: {
        type: CUSTOM_ANALYTICS_TYPES.PAGE_VIEW,
        id: 'Manage Custom Location Groups Page',
      },
      client: {
        employee_login_id: userLanId,
      },
    })
  }, [sendEvent, userLanId])

  useEffect(() => {
    makeCLGAutocompleteRequest(state.selectedVendor)
    setSelectedClg([])
  }, [state.selectedVendor])

  useEffect(() => {
    if (!existingClgDetails) {
      setGroupValues([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupLevel])

  useEffect(() => {
    if (selectedClg?.length && selectedClg[0]?.value !== 'NEW_CLG') {
      makeClgByIdRequest(selectedClg[0]?.value)
    } else if (selectedClg?.[0]?.value === 'NEW_CLG') {
      setExistingClgDetails(null)
    } else {
      setExistingClgDetails(null)
      clearInputs()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClg])

  useEffect(() => {
    if (existingClgDetails) {
      setGroupTitle(existingClgDetails?.title || '')
      setGroupDescription(existingClgDetails?.description || '')
      const matchingLevel = groupLevelOptions.find(
        (option) =>
          option?.value ===
          existingClgDetails?.locationGroups?.[0]?.[
            COST_LOCATION_GROUP_TYPE.variableName
          ],
      )
      setGroupLevel(matchingLevel ? [matchingLevel] : [])
      setGroupValues(
        existingClgDetails?.locationGroups?.[0]?.[
          COST_LOCATION_GROUP_VALUES.variableName
        ]?.map((value) => {
          return { id: value, label: value, value: value }
        }) || [],
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingClgDetails])

  const makeCLGAutocompleteRequest = (vendor) => {
    setWaitingOnClgOptionsResponse(true)
    getCustomLocationGroupOptions(vendor)
      .then((response) => {
        setWaitingOnClgOptionsResponse(false)
        if (response?.status === 200) {
          setClgOptions([
            {
              id: 'NEW_CLG',
              label: 'Create a New Custom Location Group',
              value: 'NEW_CLG',
            },
            ...response.data,
          ])
        }
      })
      .catch(() => {
        setWaitingOnClgOptionsResponse(false)
      })
  }

  const makeClgByIdRequest = (id) => {
    setWaitingOnResponse(true)
    getCustomLocationGroupById(id)
      .then((response) => {
        setWaitingOnResponse(false)
        if (response?.status === 200) {
          setExistingClgDetails(response?.data)
        } else {
          sendFailureToast(
            'Failed to get data for the selected Custom Location Group',
          )
        }
      })
      .catch(() => {
        setWaitingOnResponse(false)
        sendFailureToast(
          'Failed to get data for the selected Custom Location Group',
        )
      })
  }

  const makeUpdateClgRequest = () => {
    const requestBody = {
      customLocationGroupId: existingClgDetails?.customLocationGroupId,
      title: groupTitle,
      description: groupDescription,
      locationGroups: [
        {
          [COST_LOCATION_GROUP_TYPE.variableName]: groupLevel?.[0]?.value,
          [COST_LOCATION_GROUP_VALUES.variableName]: groupValues.map(
            (option) => option?.value,
          ),
        },
      ],
      vendorId: state.selectedVendor,
    }
    setWaitingOnResponse(true)
    updateCustomLocationGroup(
      requestBody,
      existingClgDetails?.customLocationGroupId,
    )
      .then((response) => {
        setWaitingOnResponse(false)
        if (response?.status === 200) {
          sendSuccessToast(
            'Update Successful',
            'The updates were added to the Custom Location Group',
          )
          setSelectedClg([])
          clearInputs()
          makeCLGAutocompleteRequest(state.selectedVendor)
        } else {
          sendFailureToast('Failed to apply updates to Custom Location Group')
        }
      })
      .catch(() => {
        setWaitingOnResponse(false)
        sendFailureToast('Failed to apply updates to Custom Location Group')
      })
  }

  const makeCreateNewClgRequest = () => {
    const requestBody = {
      title: groupTitle,
      description: groupDescription,
      locationGroups: [
        {
          [COST_LOCATION_GROUP_TYPE.variableName]: groupLevel?.[0]?.value,
          [COST_LOCATION_GROUP_VALUES.variableName]: groupValues.map(
            (option) => option?.value,
          ),
        },
      ],
      vendorId: state.selectedVendor,
    }
    setWaitingOnResponse(true)
    createCustomLocationGroup(requestBody)
      .then((response) => {
        setWaitingOnResponse(false)
        if (response?.status === 200) {
          sendSuccessToast(
            'Save Successful',
            'Successfully created a new Custom Location Group',
          )
          clearInputs()
          makeCLGAutocompleteRequest(state.selectedVendor)
          setSelectedClg([])
        } else {
          sendFailureToast('Failed to save new Custom Location Group')
        }
      })
      .catch(() => {
        setWaitingOnResponse(false)
        sendFailureToast('Failed to save new Custom Location Group')
      })
  }

  const makeDeleteClgRequest = () => {
    setWaitingOnResponse(true)
    deleteCustomLocationGroup(
      state.selectedVendor,
      existingClgDetails?.customLocationGroupId,
    )
      .then((response) => {
        setWaitingOnResponse(false)
        if (response?.status === 200) {
          sendSuccessToast(
            'Delete Successful',
            'Successfully deleted the Custom Location Group',
          )
          setSelectedClg([])
          setExistingClgDetails(null)
          clearInputs()
          makeCLGAutocompleteRequest(state.selectedVendor)
        } else {
          sendFailureToast('Failed to delete the Custom Location Group')
        }
      })
      .catch(() => {
        setWaitingOnResponse(false)
        sendFailureToast('Failed to delete the Custom Location Group')
      })
  }

  const sendFailureToast = (message) => {
    makeToast({
      type: 'error',
      heading: 'Error',
      message: message,
    })
  }

  const sendSuccessToast = (heading, message) => {
    makeToast({
      type: 'success',
      heading: heading,
      message: message,
    })
  }

  const clearInputs = () => {
    setGroupTitle('')
    setGroupDescription('')
    setGroupLevel([])
    setGroupValues([])
  }

  const clgAutocompleteSetter = (newValue) => {
    if (Array.isArray(newValue)) {
      setSelectedClg(newValue)
    } else {
      setSelectedClg([newValue])
    }
  }

  const groupLevelSetter = (newValue) => {
    if (Array.isArray(newValue)) {
      setGroupLevel(newValue)
    } else {
      setGroupLevel([newValue])
    }
  }

  const handleSaveClick = () => {
    if (existingClgDetails) {
      makeUpdateClgRequest()
    } else {
      makeCreateNewClgRequest()
    }
  }

  function constructGroupLevelOptions() {
    const returnOptions = []
    Object.values(MANAGE_CLG_GROUP_LEVEL_OPTIONS).forEach((field) => {
      returnOptions.push({
        id: field.variableName,
        value: field.variableName,
        label: field.label,
      })
    })
    return returnOptions
  }

  const areGroupDetailFieldsDisabled = () => {
    return (
      dvsStatus === 'PENDING' ||
      dvsStatus === true ||
      dvsStatus === undefined ||
      !selectedClg.length ||
      (selectedClg.length &&
        !existingClgDetails &&
        selectedClg?.[0]?.value !== 'NEW_CLG')
    )
  }

  const isSaveButtonDisabled = () => {
    if (
      dvsStatus === 'PENDING' ||
      dvsStatus === true ||
      dvsStatus === undefined ||
      !groupTitle ||
      groupTitle?.length > GROUP_TITLE_MAX_CHARS ||
      containsSpecialChars(groupTitle) ||
      !groupDescription ||
      groupDescription?.length > GROUP_DESC_MAX_CHARS ||
      containsSpecialChars(groupDescription) ||
      !groupLevel?.length ||
      !groupValues?.length
    ) {
      return true
    } else if (existingClgDetails) {
      return (
        groupTitle === existingClgDetails?.title &&
        groupDescription === existingClgDetails?.description &&
        isEqual(
          groupValues.map((option) => option?.value).sort(),
          existingClgDetails?.locationGroups?.[0]?.[
            COST_LOCATION_GROUP_VALUES.variableName
          ].sort(),
        )
      )
    } else {
      return false
    }
  }

  const headerSubtext = (
    <span>
      {`This is where you can create and manage Custom Location Groups,
        which will allow you to group multiple locations of the same type
        together so that you can easily search and update across multiple
        locations with a single entry.`}
    </span>
  )

  return (
    <>
      <Layout.Body
        className="hesp-generic-layout"
        data-testid="ManageCustomLocationGroupsPage"
        includeRail
      >
        <Grid.Container>
          <SectionHeader
            pageHeading="Manage Custom Location Groups"
            subtext={headerSubtext}
          />
          <Grid.Item className="hesp-general-fullwidth">
            <p className="hc-fs-md hc-mb-normal">
              <strong>Select a Custom Location Group</strong>
            </p>
            <div style={{ width: '30%', minWidth: '300px' }}>
              <AutocompleteFilter
                id="clg-autocomplete"
                label="Custom Location Group"
                optionsRequest={clgOptions}
                selectedOptions={(input) => clgAutocompleteSetter(input)}
                requestType="staticList"
                data-testid="clg-autocomplete"
                optionHeight="expanded"
                value={selectedClg}
                loadingStaticOptions={waitingOnClgOptionsResponse}
                error={dvsStatus === true}
                errorText={
                  dvsStatus === true
                    ? 'Custom Location Groups cannot be created for DVS vendors'
                    : ''
                }
              />
            </div>
          </Grid.Item>
          <Grid.Item className="hesp-general-fullwidth hc-pt-none">
            <div
              className="hesp-position-relative"
              style={{ width: '60%', minWidth: '600px' }}
            >
              {waitingOnResponse ? (
                <>
                  <span
                    data-testid="clg-group-details-upload-lockout"
                    className="hesp-lockout-container hesp-lockout-overlay"
                  ></span>
                  <span className={'hesp-lockout-container'}>
                    <Spinner size="expanded" />
                  </span>
                </>
              ) : null}
              <p className="hc-fs-md hc-mb-normal">
                <strong>Modify Group Details</strong>
              </p>
              <Input.Label htmlFor="group-title-input" required>
                Group Title
              </Input.Label>
              <Input.Text
                className="hc-mb-none"
                id="group-title-input"
                data-testid="group-title-input"
                onChange={(event) => setGroupTitle(event?.target?.value)}
                value={groupTitle}
                disabled={areGroupDetailFieldsDisabled()}
              />
              <Input.Info
                className="hc-mb-dense"
                id="group-title-info"
                error={
                  groupTitle?.length > GROUP_TITLE_MAX_CHARS ||
                  containsSpecialChars(groupTitle)
                }
              >
                {`
                ${
                  groupTitle?.length > GROUP_TITLE_MAX_CHARS
                    ? `Group Title must not exceed ${GROUP_TITLE_MAX_CHARS} characters. `
                    : ''
                }
                ${
                  containsSpecialChars(groupTitle)
                    ? 'Group Title must not contain special characters. '
                    : ''
                }`}
              </Input.Info>
              <Input.Label htmlFor="group-desc-input" required>
                Group Description
              </Input.Label>
              <Input.Textarea
                className="hc-mb-none"
                style={{ minHeight: '30px', height: '70px' }}
                id="group-desc-input"
                data-testid="group-desc-input"
                grow
                onChange={(event) => setGroupDescription(event?.target?.value)}
                value={groupDescription}
                disabled={areGroupDetailFieldsDisabled()}
              />
              <Input.Info
                className="hc-mb-dense"
                id="group-desc-info"
                error={
                  groupDescription?.length > GROUP_DESC_MAX_CHARS ||
                  containsSpecialChars(groupDescription)
                }
              >
                {`
                ${
                  groupDescription?.length > GROUP_DESC_MAX_CHARS
                    ? `Group Description must not exceed ${GROUP_DESC_MAX_CHARS} characters. `
                    : ''
                }
                ${
                  containsSpecialChars(groupDescription)
                    ? 'Group Description must not contain special characters. '
                    : ''
                }`}
              </Input.Info>
              <div className="hesp-general-flex">
                <div className="hesp-general-fullwidth">
                  <AutocompleteFilter
                    id="group-level-autocomplete"
                    label="Group Level"
                    optionsRequest={groupLevelOptions}
                    selectedOptions={(input) => groupLevelSetter(input)}
                    requestType="staticList"
                    data-testid="group-level-autocomplete"
                    required
                    value={groupLevel}
                    disabled={
                      areGroupDetailFieldsDisabled() || existingClgDetails
                    }
                  />
                </div>
                <div className="hesp-general-fullwidth hc-ml-normal">
                  <AutocompleteFilter
                    id="group-values-autocomplete"
                    label="Group Values"
                    fieldName={
                      MANAGE_CLG_GROUP_LEVEL_OPTIONS[groupLevel?.[0]?.value]
                        ?.filterDetails?.autocompleteRequestName
                    }
                    optionsRequest={
                      MANAGE_CLG_GROUP_LEVEL_OPTIONS[groupLevel?.[0]?.value]
                        ?.filterDetails?.autocompleteRequestFunc
                    }
                    sortOptions={
                      MANAGE_CLG_GROUP_LEVEL_OPTIONS[groupLevel?.[0]?.value]
                        ?.filterDetails?.sortFunc
                    }
                    selectedOptions={(input) => setGroupValues(input)}
                    requestType={
                      MANAGE_CLG_GROUP_LEVEL_OPTIONS[groupLevel?.[0]?.value]
                        ?.filterDetails?.requestType
                    }
                    data-testid="group-values-autocomplete"
                    multiselect
                    required
                    value={groupValues}
                    disabled={
                      areGroupDetailFieldsDisabled() || !groupLevel.length
                    }
                  />
                </div>
              </div>
              <div className="hesp-general-flex">
                <Button
                  type="primary"
                  onClick={handleSaveClick}
                  disabled={isSaveButtonDisabled()}
                  data-testid="save-clg-button"
                >
                  SAVE
                </Button>
                {existingClgDetails && (
                  <Button
                    className="hc-ml-md"
                    type="secondary"
                    onClick={() => setIsDeleteDialogVisible(true)}
                    disabled={
                      dvsStatus === 'PENDING' ||
                      dvsStatus === true ||
                      dvsStatus === undefined
                    }
                    data-testid="delete-clg-button"
                  >
                    <>
                      <EnterpriseIcon
                        className="hc-mr-dense"
                        icon={TrashIcon}
                      />
                      DELETE
                    </>
                  </Button>
                )}
              </div>
            </div>
            <Dialog
              headingText="Delete Custom Location Group"
              bodyText="Are you sure you want to delete this Custom Location Group?"
              isVisible={isDeleteDialogVisible}
              refuseButtonText="CANCEL"
              onRefuse={() => {
                setIsDeleteDialogVisible(false)
              }}
              approveButtonText="DELETE"
              onApprove={() => {
                makeDeleteClgRequest()
                setIsDeleteDialogVisible(false)
              }}
            />
          </Grid.Item>
        </Grid.Container>
      </Layout.Body>
    </>
  )
}
